import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Dialog, Form } from "~/components/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useUpdateLexiconConcept } from "@noa/api-client";
import type { LexiconConceptResponse } from "@noa/api-core";
import type { AppLanguageType } from "@noa/i18n";
import type { LexiconSubject } from "~/common/lexicon-subject";
import {
  firstTabWithError,
  fullLexicalConceptFormToUpdateRequestBody,
  lexiconConceptSchema,
  lexiconModalClassNames,
  subjectId,
} from "~/utils/lexicon";
import type { LexiconConceptForm } from "~/utils/lexicon";
import { LexiconConceptFields } from "./lexicon-concept-fields";

type UpdateConceptModalProps = {
  concept: LexiconConceptResponse | null;
  subjects: LexiconSubject[];
  language: AppLanguageType;
  close: () => void;
};

export function UpdateConceptModal({ language, close, concept, subjects }: UpdateConceptModalProps) {
  const { t } = useTranslation("lexicon");
  const tabsKey = `${concept?.unique ?? ""}-${language}`;
  const [activeTab, setActiveTab] = useState<string>(language);
  const [submitError, setSubmitError] = useState<string>();
  const { mutate: updateLexicalConcept, isPending: isUpdatingLexicalConcept } = useUpdateLexiconConcept();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LexiconConceptForm>({ resolver: zodResolver(lexiconConceptSchema) });

  // The concept's own subject, always offered even if it is missing from the
  // caller's list. A concept stores both translations of its subject, so its own
  // is selectable by construction; without this, editing a concept filed under a
  // subject that has since lost a translation silently blanks a populated field
  // and offers no way to put it back.
  const selectableSubjects = useMemo(() => {
    if (!concept) return subjects;

    const own: LexiconSubject = {
      fr: concept.subject_fr,
      en: concept.subject_en,
      id: subjectId({ fr: concept.subject_fr, en: concept.subject_en }),
    };

    // Matched on the whole pair, not on `id` alone. The id is only the French
    // label, so a list subject sharing it but carrying a different English one
    // would shadow the concept's own pair — and saving would then rewrite the
    // concept's `subject_en` to the list's, silently, on an entity with no author
    // field to trace it by.
    const alreadyOffered = subjects.some((s) => s.fr === own.fr && s.en === own.en);
    if (alreadyOffered) return subjects;

    // Prepended, not appended: `fullLexicalConceptFormToUpdateRequestBody`
    // resolves the subject with `find` on that same non-unique id, so the
    // concept's own pair has to come first to win the collision.
    return [own, ...subjects];
  }, [concept, subjects]);

  // Seeds when the modal opens on a different concept — keyed on its identity,
  // not on the subjects list. The subject query is invalidated whenever anyone
  // creates a concept (including from the chat card), so depending on the list
  // meant a background refetch mid-edit re-ran `reset()` and silently threw away
  // whatever the user had typed.
  //
  // The subject is derived from the concept itself rather than looked up, so the
  // seeding no longer needs the list at all: `selectableSubjects` guarantees this
  // id is among the options.
  useEffect(() => {
    if (!concept) return;

    reset({
      term_fr: concept.term_fr,
      term_en: concept.term_en,
      description_fr: concept.description_fr,
      description_en: concept.description_en,
      subject_id: subjectId({ fr: concept.subject_fr, en: concept.subject_en }),
      acronyms_fr: concept.acronyms_fr,
      acronyms_en: concept.acronyms_en,
      synonyms_fr: concept.synonyms_fr,
      synonyms_en: concept.synonyms_en,
    });
  }, [concept, reset]);

  // Now that the tab is controlled, `tabsKey`'s remount no longer resets it —
  // follow the app language ourselves so a language switch still lands on it.
  useEffect(() => {
    setActiveTab(language);
  }, [language]);

  const onSubmit = (values: LexiconConceptForm) => {
    if (!concept) return;

    setSubmitError(undefined);

    const body = fullLexicalConceptFormToUpdateRequestBody(values, selectableSubjects);

    // Backstop: the options are pre-filtered to bilingual subjects, so this is
    // only reachable if one lost a translation between load and submit. Say so
    // — returning silently is what made the OK button look dead.
    if (!body) {
      setSubmitError(t("errors.subjectNotTranslated"));
      return;
    }

    updateLexicalConcept(
      {
        unique: concept.unique,
        body,
        previousSubjectEn: concept.subject_en,
        previousSubjectFr: concept.subject_fr,
      },
      {
        onSuccess: () => {
          close();
          reset();
        },
        onError: () => setSubmitError(t("errors.submitFailed")),
      }
    );
  };

  // Validation errors on a hidden tab are invisible, so a plain OK looks like a
  // dead button. Reveal the offending tab instead. Takes the errors react-hook-form
  // passes: the `formState` ones are still empty this tick, on the first submit.
  const revealInvalidTab = useCallback(
    (submitErrors: FieldErrors<LexiconConceptForm>) => {
      const target = firstTabWithError(submitErrors, activeTab === "en" ? "en" : "fr");
      if (target) setActiveTab(target);
    },
    [activeTab]
  );

  const handleCancel = () => {
    close();
    reset();
    setSubmitError(undefined);
  };

  return (
    <Dialog
      title={t("updateConceptModal.title")}
      open={!!concept}
      onOk={handleSubmit(onSubmit, revealInvalidTab)}
      onCancel={handleCancel}
      okText={t("actions.update")}
      cancelText={t("common:cancel")}
      confirmLoading={isUpdatingLexicalConcept}
      classNames={lexiconModalClassNames}
    >
      <Form>
        <LexiconConceptFields
          control={control}
          errors={errors}
          language={language}
          subjects={selectableSubjects}
          tabsKey={tabsKey}
          activeKey={activeTab}
          onTabChange={setActiveTab}
        />
        {submitError && <Alert type="error" message={submitError} showIcon className="mt-2" />}
      </Form>
    </Dialog>
  );
}
